/**
 * AI画像生成 SSE API
 */
import { fetchAuthToken } from "app/Auth";
import Constants from "common/constant";
import type {
  GenerateImageParams,
  ImageGenerationSSECallbacks,
  AIGeneratedAsset,
  AIImageUsageInfo,
} from "../types";

/**
 * SSEイベントハンドラ
 */
const handleImageSSEEvent = (
  event: string,
  data: Record<string, unknown>,
  callbacks: ImageGenerationSSECallbacks
) => {
  switch (event) {
    case "generation_start":
      callbacks.onStart(data.requestId as string);
      break;
    case "partial_image":
      callbacks.onPartialImage(
        data.b64Json as string,
        data.progress as number,
        data.index as number,
        data.imageIndex as number | undefined
      );
      break;
    case "generation_complete": {
      const imageIndex = data.imageIndex as number | undefined;
      callbacks.onComplete(
        data.imageUrl as string,
        data.id as number,
        0,
        imageIndex
      );
      break;
    }
    case "all_complete": {
      const images = data.images as AIGeneratedAsset[];
      const usage = data.usage as AIImageUsageInfo | undefined;
      callbacks.onAllComplete?.(images, usage);
      break;
    }
    case "error":
      callbacks.onError(data.code as string, data.message as string);
      break;
  }
};

/**
 * AI画像生成（SSE Stream）
 */
export const generateImageSSE = async ({
  uid,
  options,
  purpose = "child_profile_icon",
  callbacks,
  signal,
}: GenerateImageParams): Promise<void> => {
  const token = await fetchAuthToken();
  const url = uid
    ? `${Constants.apiHost}/v1/ai/image/users/${uid}/generate`
    : `${Constants.apiHost}/v1/ai/image/generate`;

  const body = {
    purpose,
    params: {
      gender: options.gender || "none",
      age: options.age,
      favorite_colors: options.favorite_colors,
      self_introduction: options.self_introduction,
      best_subjects: options.best_subjects,
      glasses: options.glasses,
      accessory: options.accessory,
      hairstyle: options.hairstyle,
      body_type: options.body_type,
      face_shape: options.face_shape,
      occupation: options.occupation,
      job_title: options.job_title,
      organization: options.organization,
      region: options.region,
      city: options.city,
      household_members: options.household_members,
      boys_count: options.boys_count,
      girls_count: options.girls_count,
      industry: options.industry,
      annual_income: options.annual_income,
      additional_prompt: options.additional_prompt,
    },
    partialImages: 3,
  };

  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: token,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    if (response.status === 403) {
      try {
        const body = await response.json();
        callbacks.onError(
          body.code || "ACCESS_DENIED",
          body.message || "この機能はご利用いただけません"
        );
      } catch {
        callbacks.onError("ACCESS_DENIED", "この機能はご利用いただけません");
      }
      return;
    }
    callbacks.onError("HTTP_ERROR", `HTTP error: ${response.status}`);
    return;
  }

  const reader = response.body?.getReader();
  if (!reader) {
    callbacks.onError("NO_READER", "Response body is not readable");
    return;
  }

  const decoder = new TextDecoder();
  let buffer = "";
  let currentEvent = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";
      for (const line of lines) {
        if (line.startsWith("event: ")) {
          currentEvent = line.slice(7);
        } else if (line.startsWith("data: ")) {
          const data = line.slice(6);
          try {
            const parsed = JSON.parse(data);
            handleImageSSEEvent(currentEvent, parsed, callbacks);
          } catch {
            // JSON parse error, skip
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
};
